import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import pt from '../../locales/pt';
import { usePatchCurrentCycleMutation } from '../../features/snapshots/queries';
import { useAuth } from '../../store/AuthContext';
import { extractMutationError } from '../../utils/apiError';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

/**
 * Início e fim do ciclo actual; ao guardar, o dia da selfie/lembrete fica alinhado ao fim.
 */
export function CyclePeriodSettings({ cycle }) {
  const patchCycle = usePatchCurrentCycleMutation();
  const { refreshMe } = useAuth();
  const [start, setStart] = useState(cycle?.start ?? '');
  const [end, setEnd] = useState(cycle?.end ?? '');

  useEffect(() => {
    if (cycle?.start) setStart(cycle.start);
    if (cycle?.end) setEnd(cycle.end);
  }, [cycle?.start, cycle?.end]);

  if (!cycle?.isCurrent) {
    return (
      <p className="text-sm text-muted-foreground">{pt.cycleSettings.currentCycle.readOnlyHint}</p>
    );
  }

  const onSave = async (event) => {
    event.preventDefault();
    if (!start || !end) return;
    try {
      await patchCycle.mutateAsync({ start, end });
      await refreshMe();
      toast.success(pt.cycleSettings.saved);
    } catch (err) {
      toast.error(extractMutationError(err, pt.cycleSettings.saveError));
    }
  };

  return (
    <form
      key={`${cycle.start}-${cycle.end}`}
      className="flex flex-col gap-4"
      onSubmit={onSave}
    >
      <p className="text-sm text-muted-foreground">
        {pt.cycleSettings.currentCycle.periodLabel.replace('{period}', cycle.label)}
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-muted-foreground" htmlFor="cycle-start-date">
            {pt.cycleSettings.currentCycle.startLabel}
          </label>
          <Input
            id="cycle-start-date"
            type="date"
            value={start}
            min={cycle.minStart ?? undefined}
            max={cycle.maxStart ?? undefined}
            onChange={(event) => setStart(event.target.value)}
            required
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-muted-foreground" htmlFor="cycle-end-date">
            {pt.cycleSettings.currentCycle.endLabel}
          </label>
          <Input
            id="cycle-end-date"
            type="date"
            value={end}
            min={cycle.minEnd ?? cycle.minStart ?? undefined}
            max={cycle.maxEnd ?? undefined}
            onChange={(event) => setEnd(event.target.value)}
            required
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={patchCycle.isPending || !start || !end}>
          {patchCycle.isPending ? pt.common.loading : pt.common.save}
        </Button>
      </div>
    </form>
  );
}
